import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { normalizeProject, statusKey } from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { buildScheduleView } from "./rdSummary";
import useGoalBufferDays from "./useGoalBufferDays";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, Field, RowButton, StatusChip } from "../../components/mui";
import { MdCheck, MdCircle, MdErrorOutline, MdRemove, MdWarningAmber } from "react-icons/md";

/**
 * 時程 — 新增里程碑（設計稿 #screen-goal-new，el-pm-template_4.html L1708-1785）
 *
 * 「里程碑」在資料庫裡就是既有的 ProjectPhase（里程碑），零新端點：
 * 建立走 POST /Phase/{projectId}，修改走 PUT /Phase/{phaseId}，
 * 兩支都是純 FormData（name/description/startDate/endDate/sortOrder/confirmer），
 * 不是 data=<JWT> 那條路 —— 送錯形狀後端會整筆收到 null。
 *
 * 這一頁同時是「修改里程碑」的畫面：/Schedule（Goals.js）的「修改里程碑」與
 * 下面那份既有里程碑清單都會帶 ?phaseId= 進來。沒有它就是新增。
 * 兩者共用同一張表單，因為要填的東西一模一樣，分成兩頁只會讓文案各寫一套。
 *
 * 版面：外層不再包一層頁面容器（Layout.js 的內容區已經處理寬度與內距），
 * 也不自己寫 max-width —— 設計稿在卡片
 * 上寫死的 640px 是它自己那張圖的版面，放進這個系統的內容區之後右邊會空掉一大塊
 * （見表單那一段的說明）。
 */

/* ===================== 取值與格式 ===================== */

/** 後端的 DateTimeOffset → 當地時區的 "YYYY-MM-DD"（理由同 Goals.js 的 ymdOf） */
function ymdOf(v) {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

/** "2026-10-15" → 「10 月 15 日」；跨年才補年份 */
function longDate(ymd) {
  if (!ymd) return "";
  const d = dayjs(ymd);
  if (!d.isValid()) return "";
  return d.isSame(dayjs(), "year") ? d.format("M 月 D 日") : d.format("YYYY 年 M 月 D 日");
}

/**
 * 人名那一格要寫什麼（與 Goals.js 的 personLabel 同一份規則）。
 *
 * 後端是拿帳號去 Admin 表換姓名，帳號被刪掉時換不回來、回的是空字串。
 * 換不到姓名就退回帳號 —— 帳號是那筆資料上真的存在的值；兩個都沒有才回空字串，
 * 由呼叫端整格不渲染。任何情況都不編一個名字上去。
 */
function personLabel(name, account) {
  const n = String(name || "").trim();
  return n || String(account || "").trim();
}

/**
 * 既有里程碑列的徽章。與 /Schedule 的里程碑卡同一份規則（都吃 rdSummary 的 level），
 * 只是這裡的列比較窄，話講短一點。兩頁各寫一套的話，遲早會出現
 * 「時程軸說超過 4 天、這一頁說還有緩衝」。
 */
function goalTag(g) {
  /* 里程碑的狀態完全由底下的工項推導：工項全部完成＝達成 */
  if (g.level === "done") return { kind: "done", mark: <MdCheck />, text: "已完成 · 工項全數完成" };
  /* 沒有工項的里程碑：判定不了完成、也判定不了逾期，灰色、只講事實（見 rdSummary 的 empty） */
  if (g.level === "empty") {
    return {
      kind: "closed",
      mark: <MdRemove />,
      text: g.daysLeft !== null && g.daysLeft < 0 ? `已過里程碑日期 ${-g.daysLeft} 天 · 尚無工項` : "未開始 · 尚無工項",
    };
  }
  /* 沒訂 end_date 的里程碑算不出任何天數，寫「剩 0 天」會是錯的，直接說沒訂 */
  if (!g.endYmd) return { kind: "closed", mark: <MdRemove />, text: "未設定里程碑日期" };
  if (g.level === "attention") {
    return {
      kind: "attention",
      mark: <MdWarningAmber />,
      text: g.overrunDays > 0 ? `超出 ${g.overrunDays} 天` : "已過里程碑日期",
    };
  }
  if (g.level === "warn") return { kind: "warn", mark: <MdErrorOutline />, text: `緩衝剩 ${g.bufferLeft} 天` };
  return { kind: "progress", mark: <MdCircle size="0.6em" />, text: "進度正常" };
}

/* ===================== 共用零件 ===================== */

/**
 * 表單欄位一律用共用的 Field（components/mui），結構與 components/design/EntityForm.js 一致
 * （上方粗體標籤＋必填／選填、說明、欄位、錯誤），兩邊長得一樣，使用者才不會覺得這一頁是別的系統。
 *
 * 「新增里程碑」有兩個入口 —— 這一頁（/Schedule/Goal/New）與專案詳情裡走
 * EntityForm formConfig("phases") 的那一張。EntityForm 已經改成三種標記都明講
 * （必填／選填／系統自動填），這裡就必須跟著標「選填」：同一張表單在兩個入口
 * 長得不一樣，使用者剛在一邊學會「沒有標記＝系統會明講選填」，換到這邊
 * 就分不出「這格可以不填」還是「我漏看了必填」。
 *
 * 沒有直接用 EntityForm：這張表單的卡片裡夾了一條警示 Banner（設計稿 L1738），
 * 而 EntityForm 沒有卡片內的插槽；而且「提前幾天開始提醒」那一格是一段純文字說明、
 * 不是欄位，那也不是 forms.js 的欄位定義表達得出來的。
 */

/** 欄位的 aria-describedby：說明一定有，錯誤有才加 */
const describedBy = (id, error) => (error ? `${id}-help ${id}-err` : `${id}-help`);

/** 既有里程碑那一列。比 Today 的 ElRow 多一個 note（右側的「修改」文案） */
function GoalRow({ tone, title, metas, tag, note, onClick }) {
  return (
    <RowButton
      tone={tone}
      onClick={onClick}
      title={title}
      meta={
        metas.length > 0
          ? metas.map((m, i) => <span key={`${i}-${m}`}>{m}</span>)
          : null
      }
      side={
        <>
          <StatusChip kind={tag.kind} mark={tag.mark}>
            {tag.text}
          </StatusChip>
          <Typography variant="body2" color="text.secondary" component="span">
            {note}
          </Typography>
        </>
      }
    />
  );
}

GoalRow.propTypes = {
  tone: PropTypes.string,
  title: PropTypes.string.isRequired,
  metas: PropTypes.array,
  tag: PropTypes.object.isRequired,
  note: PropTypes.string,
  onClick: PropTypes.func,
};
GoalRow.defaultProps = { tone: "", metas: [], note: "", onClick: () => {} };

/* ===================== 常數 ===================== */

/* ===================== 畫面 ===================== */

function ScheduleGoalNew(props) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast, userInfo } = useApp();
  const { GET_ProjectList, GET_PhaseList, GET_ScheduleWorkItems, GET_ProjectMembers, POST_Phase, PUT_Phase } = props;

  /* 從時程軸帶過來的專案與（修改時的）里程碑。兩個都是網址參數，直接讀取即可 */
  const qProject = params.get("projectId") || "";
  const editId = params.get("phaseId") || "";
  /*
   * 「這一頁是不是修改模式」由網址決定，不等資料回來。
   * 用載好的那筆資料判斷的話，載入那一秒標題會先寫「新增里程碑」再跳成「修改」，
   * 按鈕也會先寫「訂下這個里程碑」—— 那一秒剛好是最容易被按下去的時候。
   */
  const isEdit = !!editId;

  const [projSel, setProjSel] = useState(qProject);
  const [values, setValues] = useState({
    name: "",
    date: "",
    owner: "",
    note: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [ready, setReady] = useState(false);

  const todayYmd = dayjs().format("YYYY-MM-DD");

  useEffect(() => {
    GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
  }, [GET_ProjectList]);

  const projects = useMemo(() => {
    const arr = Array.isArray(props.projectList) ? props.projectList : _.get(props.projectList, "data_list", []);
    return (Array.isArray(arr) ? arr : []).filter(Boolean).map((p) => normalizeProject(p));
  }, [props.projectList]);

  /* 網址沒帶專案時（例如從命令面板直接開這一頁）退回第一個專案，
     頁面才不會停在一張送不出去的空表單上。使用者仍可以用下拉改。 */
  useEffect(() => {
    if (!projSel && projects.length) setProjSel(String(projects[0].id));
  }, [projects, projSel]);

  useEffect(() => {
    if (!projSel) return;
    setReady(false);
    GET_PhaseList(Number(projSel), null, (v) => {
      if (!v) setReady(true);
    });
    /* 既有里程碑那一份清單要標黃／紅，而燈號是由底下工項的截止日算出來的，
       所以連工項一起拉；件數則直接用 /Phase/List 回的 task_count */
    /*
     * take 要寫出來。
     *
     * /Schedule/WorkItems 的預設是 200 筆（ScheduleController，上限 500），而排序是
     * ProjectId → StartDate → DueDate —— 被截掉的正好是起始日最晚的那一批，
     * 也就是最可能決定 lastEndYmd（工項做完預計哪一天）的那幾件。
     * 不帶 take 的話，超過 200 件工項的專案在這一頁會看不到最晚那幾件、算出綠燈，
     * 而排程讀的是全部工項、算出紅燈寄信 —— 同一個里程碑畫面與信完全相反。
     * 500 是後端夾住的上限，再大也沒有用；真的超過 500 件的專案要改成分頁把整份撈完。
     */
    GET_ScheduleWorkItems({ projectId: Number(projSel), take: 500 }, null, null);
    /* 「誰負責確認」的下拉：選項是這個專案的成員（顯示姓名、送出帳號），
       不是讓人自己打字 —— 打錯字的帳號存進去之後畫面上就換不回姓名 */
    GET_ProjectMembers({ ProjectId: Number(projSel) }, null, null);
  }, [projSel, GET_PhaseList, GET_ScheduleWorkItems, GET_ProjectMembers]);

  /* ---------- 既有里程碑 ---------- */

  const goalsRaw = useMemo(() => {
    const arr = Array.isArray(props.phaseList) ? props.phaseList : _.get(props.phaseList, "data_list", []);
    return (Array.isArray(arr) ? arr : [])
      .filter(Boolean)
      .filter((p) => String(_.get(p, "project_id", projSel)) === String(projSel))
      .map((p) => ({
        id: _.get(p, "id"),
        name: String(_.get(p, "name") || "未命名的里程碑"),
        desc: String(_.get(p, "description") || ""),
        endYmd: ymdOf(_.get(p, "end_date")),
        startYmd: ymdOf(_.get(p, "start_date")),
        createdYmd: ymdOf(_.get(p, "create_at")),
        /* 訂定者與確認人。/Phase/List 兩組都回（帳號 ＋ 換好的姓名，換不到是空字串）。
           confirmer 是帳號，修改模式要拿它把下拉選回原本那個人；
           confirmerName 只給畫面看。這裡的欄位名與 Goals.js 保持一致，
           兩頁餵給 rdSummary 的形狀不一樣的話，同一個里程碑在兩頁會長得不一樣 */
        createdBy: personLabel(_.get(p, "create_name"), _.get(p, "create_id")),
        confirmer: String(_.get(p, "confirmer") || ""),
        confirmerName: personLabel(_.get(p, "confirmer_name"), _.get(p, "confirmer")),
        statusCode: Number(_.get(p, "status") || 0),
        sortOrder: Number(_.get(p, "sort_order") || 0),
        taskCount: Number(_.get(p, "task_count") || 0),
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [props.phaseList, projSel]);

  const tasksRaw = useMemo(() => {
    const arr = Array.isArray(props.workItems) ? props.workItems : _.get(props.workItems, "data_list", []);
    return (Array.isArray(arr) ? arr : [])
      .filter(Boolean)
      .filter((t) => String(_.get(t, "project_id")) === String(projSel))
      .map((t) => ({
        id: _.get(t, "id"),
        title: String(_.get(t, "title") || "未命名工項"),
        assignee: String(_.get(t, "assignee_name") || ""),
        startYmd: ymdOf(_.get(t, "start_date")),
        dueYmd: ymdOf(_.get(t, "due_date")),
        statusKey: statusKey(_.get(t, "status")),
        phaseId: _.get(t, "phase_id"),
      }));
  }, [props.workItems, projSel]);

  /*
   * 黃燈的門檻（「設定 → 偏好設定」的里程碑提醒門檻）。
   * 這一頁與 /Schedule 一定要吃同一個值，不然這裡的既有里程碑標黃、時程軸卻說還好。
   */
  const { bufferDays, loaded: bufferLoaded, loading: bufferLoading } = useGoalBufferDays();

  /* 燈號與 /Schedule 用同一支彙總，兩頁才不會各說各話（holidayMap 不帶：
     這一頁只用得到 level，用不到「扣掉假日還有幾個上班日」那一句） */
  const view = useMemo(
    () => buildScheduleView({ phases: goalsRaw, tasks: tasksRaw, todayYmd, bufferDays }),
    [goalsRaw, tasksRaw, todayYmd, bufferDays]
  );

  const editing = useMemo(
    () => (editId ? view.goals.find((g) => String(g.id) === String(editId)) || null : null),
    [view.goals, editId]
  );

  /*
   * 修改模式：資料回來之後把既有值鋪一次就好。
   * 用 ref 記「已經鋪過哪一筆」而不是每次 render 都鋪 —— 每次都鋪的話，
   * 使用者打字打到一半會被後端的舊值蓋回去。
   */
  const filledFor = useRef("");
  useEffect(() => {
    if (!editing || filledFor.current === String(editing.id)) return;
    filledFor.current = String(editing.id);
    setValues((prev) => ({
      ...prev,
      name: editing.name,
      date: editing.endYmd,
      note: editing.desc,
      /* 確認人鋪的是帳號不是姓名：下拉的 value 就是帳號，
         而且姓名會改、帳號不會。沒有確認人時要鋪成空字串（＝「還沒決定」），
         不能留著上一筆的值 —— 換一筆改的時候會把別人的確認人搬過來 */
      owner: editing.confirmer || "",
    }));
  }, [editing]);

  /* ---------- 確認人下拉的選項 ---------- */

  /*
   * 選項顯示姓名、送出帳號：project_phase.confirmer 存的是帳號字串。
   * 存姓名的話同名的兩個人分不出來，改過名字也對不回人。
   *
   * 最後那一段是補一筆現在的確認人。修改一個確認人已經被移出專案的里程碑時，
   * 那個帳號不在下拉裡，<select> 會顯示成什麼都沒選，使用者按下儲存就把人洗掉了，
   * 而且從頭到尾不知道發生過這件事。把它補成一個選項，要不要換掉是使用者自己決定的。
   *
   * 「已經不在成員名單裡」這句話只有在成員清單真的回來過才能寫。
   * /Phase/List 與 /Project/Member/List 是同時發出、互不等待的兩支，前者先回是常態；
   * 而 /Project/Member/List 掛的是 PermissionOnly("project")，403 或呼叫失敗時 saga 只吞掉
   * 錯誤，props.members 會永遠是空的。拿「清單是空的」當「這個人不在專案裡」的話，
   * 一個還在專案裡的確認人會被畫面指控成已經被移出去，使用者就會去把他換掉。
   * 所以 seen 是空的時候只寫名字，不下這個判斷。
   */
  const memberOptions = useMemo(() => {
    const arr = Array.isArray(props.members) ? props.members : _.get(props.members, "data_list", []);
    const seen = new Set();
    const list = [];
    (Array.isArray(arr) ? arr : []).filter(Boolean).forEach((m) => {
      const account = String(_.get(m, "account", _.get(m, "Account", "")) || "").trim();
      if (!account || seen.has(account)) return;
      seen.add(account);
      list.push({ account, label: personLabel(_.get(m, "name", _.get(m, "Name", "")), account) });
    });
    list.sort((a, b) => a.label.localeCompare(b.label, "zh-Hant"));

    const keep = String(values.owner || "").trim();
    if (keep && !seen.has(keep)) {
      const who = personLabel(editing && editing.confirmerName, keep);
      list.push({ account: keep, label: seen.size > 0 ? `${who}（已非本專案成員）` : who });
    }
    return list;
  }, [props.members, values.owner, editing]);

  /* ---------- 送出 ---------- */

  const set = (key, v) => {
    if (errors[key]) setErrors((e) => ({ ...e, [key]: "" }));
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  /*
   * 表單與「已經有的里程碑」改成兩個分頁。
   *
   * 兩段疊在同一頁的實際長度：一張七個欄位的表單，底下再接一份可能十幾列的
   * 清單 —— 要訂下一個里程碑得先捲過去確認有沒有重複，訂完又被推到清單上方，
   * 兩件事互相擋路。分成分頁之後，兩邊都在第一個畫面裡看得完。
   * 用 MUI Tabs（時程頁與審核頁都是這一套），不自己做切換器。
   *
   * 修改模式預設停在表單：使用者是從清單點「修改」進來的，目的明確。
   */
  const [tab, setTab] = useState("form");

  const back = () => {
    /*
     * 返回時把專案帶回去。
     *
     * 時程頁的專案選擇記在網址上（見 Goals.js 的 setProjSel），這裡不帶的話
     * 回去會掉回「清單的第一個專案」—— 改一個里程碑就要重選一次專案。
     */
    navigate(projSel ? `/Schedule?projectId=${encodeURIComponent(projSel)}` : "/Schedule");
  };

  /* 儲存成功後離開：回到進表單之前的那一頁（表單不留在歷史裡，返回才不會掉回送出過的表單）；
     沒有上一頁（直接開網址進來）就以 replace 換成時程頁 */
  const leaveAfterSave = () => {
    const url = projSel ? `/Schedule?projectId=${encodeURIComponent(projSel)}` : "/Schedule";
    const idx = window.history.state && typeof window.history.state.idx === "number" ? window.history.state.idx : 0;
    if (idx > 0) navigate(-1);
    else navigate(url, { replace: true });
  };

  const submit = () => {
    if (sending) return;

    const name = String(values.name || "").trim();
    const date = String(values.date || "").trim();
    const note = String(values.note || "").trim();
    const bad = {};
    if (!name) bad.name = "請填寫里程碑名稱";
    if (!date) bad.date = "請選擇里程碑日期";
    /* 達成條件照設計稿 L1730 標成必填，這裡就真的擋下來 ——
       標了卻不擋會讓人摸不著頭緒，而沒有驗收條件的里程碑等於沒有里程碑 */
    if (!note) bad.note = "請填寫達成標準";
    setErrors(bad);
    if (Object.keys(bad).length > 0) return;
    if (!projSel) {
      toast("請先選擇專案", "error");
      return;
    }
    /*
     * 網址說要改某一筆，但那一筆載不回來（被刪掉、或網址裡的專案跟里程碑對不起來）時
     * 一定要停住。少了這道，接下來會掉進新增那條路，使用者以為在改，實際上多出一筆。
     */
    if (isEdit && !editing) {
      toast("找不到該里程碑，請返回時程軸重新選擇", "error");
      return;
    }

    /*
     * POST/PUT /Phase 是 [FromForm] 的散裝參數，不是 data=<JWT>。
     * 這裡送後端真的有欄位的五項：
     *   name        ← 這個時間點要交出什麼
     *   description ← 要達成什麼才算數
     *   endDate     ← 哪一天要交
     *   confirmer   ← 誰負責確認有沒有做到（帳號字串，空字串＝還沒決定）
     *   sortOrder   ← 排序（新的排最後，與 pages/Project/Detail.js 的里程碑一致）
     * 只有「提醒緩衝天數」仍然不送：那是全公司共用的設定（system_setting 的
     * goal.buffer_days），project_phase 沒有「這個里程碑自己的門檻」這個欄位。
     */
    const fd = new FormData();
    fd.append("name", name);
    fd.append("description", note);
    fd.append("endDate", date);
    /* 沒選也要送。修改模式是整筆覆蓋，這一欄整個不出現在 FormData 裡的話，
       「使用者把確認人改回還沒決定」跟「這一欄根本沒被碰過」在送出去的東西上
       長得一模一樣 —— 送空字串才表達得出「拿掉」。 */
    fd.append("confirmer", String(values.owner || "").trim());

    if (editing) {
      /* 整筆覆蓋：表單沒有的欄位要帶回原值，否則開始日與排序會被清掉。
         status 是 PutPhase 簽章上的必填參數，所以要送；但它送進去之後會被忽略
         （PhaseService.PutPhase 一行都沒有碰 phase.Status —— 里程碑狀態改由底下的工項
         推導）。帶原值而不是填 0 只是為了不要讓送出去的東西自己說謊，
         不是在保護狀態；哪天真的要改狀態，要動的是後端，不是這一行。 */
      fd.append("status", editing.statusCode);
      fd.append("sortOrder", editing.sortOrder || 0);
      if (editing.startYmd) fd.append("startDate", editing.startYmd);
      PUT_Phase(
        Number(editing.id),
        fd,
        () => {
          toast("里程碑已更新");
          leaveAfterSave();
        },
        setSending
      );
      return;
    }

    fd.append("sortOrder", goalsRaw.length + 1);
    POST_Phase(
      Number(projSel),
      fd,
      () => {
        toast("里程碑已建立");
        /*
         * 訂完回時程軸，不要直接跳到「排工項」。
         *
         * 里程碑不需要綁工項就成立：PM／高階主管的工作就是把「哪一天要交出
         * 什麼」訂下來，把工項排進里程碑是 RD 主管的事（schedule.edit，另一顆按鈕）。
         * 原本訂完就跳排工項，等於把別人的工作推到 PM 面前，而且他多半沒有那個
         * 權限，過去只會看到一頁按不動的東西。回到時程軸還能接著訂下一個里程碑。
         */
        leaveAfterSave();
      },
      setSending
    );
  };

  /* ---------- 文案 ---------- */

  const proj = projects.find((p) => String(p.id) === String(projSel));
  const myName = _.get(userInfo, "name", _.get(userInfo, "Name", ""));
  const myRole = _.get(userInfo, "role", _.get(userInfo, "Role", ""));

  /*
   * 中文句子先拼成字串再交給 JSX。
   * 直接寫成多行 JSX 文字節點的話，Babel 會在換行處補一個半形空格，
   * 中文句子中間就會多出一個看得見的空隙。
   */
  const whoAmI = myName
    ? `目前身分：${myName}${myRole ? `（${myRole}）` : ""}，建立者將記錄為您。`
    : "";

  /*
   * 「訂下去會發生什麼」照實寫，不照設計稿的範本句照抄。
   * 設計稿寫「系統會通知 RD 主管來排工項」—— 後端 PostPhase 實際做的是
   * NotifyProject：推一則即時通知給這個專案的成員群組，只有當下開著系統的人
   * 收得到，不寄信、也不特別點名 RD 主管。寫成設計稿那樣就是承諾一件做不到的事。
   */
  /*
   * 門檻的數字要講清楚它是「有人設定過的值」還是「讀不到時的預設值」：
   * 使用者會拿它跟偏好設定頁上的數字對照，講錯就變成一個他驗證不了的說法。
   */
  /* 三種狀態要講三句話：讀到了／還在讀／真的讀不到。
     「還在讀」講成「讀不到」的話，每次進這一頁都會先看到一句假的系統故障訊息 */
  const bufferSrc = bufferLoaded
    ? "此天數為全公司共用的門檻，可於「設定 → 偏好設定」變更。"
    : bufferLoading
      ? "此天數為門檻預設值，目前生效的設定讀取中。"
      : "此天數為無法讀取設定時採用的預設值，實際門檻請見「設定 → 偏好設定」。";

  const warnBody = `里程碑建立後將立即顯示於時程軸，目前在線上的專案成員會收到一則即時通知（不寄送通知信）。之後所屬工項的緩衝不足 ${bufferDays} 天時轉為黃燈，超過里程碑日期時轉為紅燈。${bufferSrc}`;

  return (
    <>
      <Typography variant="h1" component="h1">
        {isEdit ? "編輯里程碑" : "新增里程碑"}
      </Typography>
      <Typography sx={{ mt: "var(--space-3)" }}>
        {proj
          ? `為「${proj.name}」設定里程碑。設定完成後，RD 主管即可將工項安排至該里程碑下。`
          : "設定里程碑。設定完成後，RD 主管即可將工項安排至該里程碑下。"}
      </Typography>
      {whoAmI ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-2)" }}>
          {whoAmI}
        </Typography>
      ) : null}

      {/* ---------- 分頁列 ---------- */}
      {/* 沒有既有里程碑時不畫分頁（硬規則 2）：只有一個分頁的切換器等於沒有 */}
      {ready && view.goals.length > 0 ? (
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="新增里程碑"
          sx={{ mt: "var(--space-5)" }}
        >
          <Tab value="form" label={isEdit ? "編輯里程碑" : "新增里程碑"} />
          <Tab value="list" label={`既有里程碑 ${view.goals.length}`} />
        </Tabs>
      ) : null}

      {/* ---------- 表單（設計稿 L1714-1751） ---------- */}
      {/*
        設計稿把這張卡寫死 max-width:640px。那是設計稿自己的版面，真的放進
        這個系統的內容區之後右邊會空掉一大塊 —— 這一頁沒有第二欄可以放東西，
        整塊空白不買到任何東西。改成撐滿。
      */}
      {tab === "form" ? (
      <Card sx={{ mt: "var(--space-6)" }}>
        <CardContent>
        <Stack spacing={2}>
          {/*
            設計稿沒有這一欄：它假設你是從某個專案的時程軸點進來的。
            但這一頁也可能從命令面板或網址直接開，那時候沒有專案就送不出去。
            與其讓人填完才發現送不出，不如把它畫出來 —— 從時程軸進來時已經選好了。
          */}
          <Field
            htmlFor="gn-proj"
            label="所屬專案"
            mark="req"
            help={
              isEdit
                ? "編輯里程碑時無法變更專案：變更專案等同將里程碑移至其他專案，所屬工項將全部無法對應。"
                : "自時程軸進入時，將自動帶入先前檢視的專案。"
            }
            helpId="gn-proj-help"
          >
            {/* 停用欄位要看得出來是停用的（同 EntityForm）：給得動卻存不進去比直接鎖起來更糟。
                MUI 的 disabled 樣式本身就是灰底、不可點 */}
            <TextField
              select
              fullWidth
              id="gn-proj"
              value={projSel}
              disabled={isEdit}
              onChange={(e) => setProjSel(e.target.value)}
              slotProps={{ select: { native: true }, htmlInput: { "aria-describedby": describedBy("gn-proj") } }}
            >
              {projects.length === 0 ? <option value="">載入中…</option> : null}
              {projects.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </TextField>
          </Field>

          <Field
            htmlFor="gn-name"
            label="里程碑名稱"
            mark="req"
            help="請使用客戶易於理解的說法，例如「交付初版給客戶」「正式上線」。"
            error={errors.name}
            helpId="gn-name-help"
            errorId="gn-name-err"
          >
            <TextField
              fullWidth
              id="gn-name"
              placeholder="交付初版給客戶"
              value={values.name}
              error={!!errors.name}
              onChange={(e) => set("name", e.target.value)}
              slotProps={{ htmlInput: { "aria-describedby": describedBy("gn-name", errors.name) } }}
            />
          </Field>

          <Field
            htmlFor="gn-date"
            label="里程碑日期"
            mark="req"
            help="此日期將成為時程上的里程碑，所有工項皆須安排在此日期之前。"
            error={errors.date}
            helpId="gn-date-help"
            errorId="gn-date-err"
          >
            <TextField
              fullWidth
              id="gn-date"
              type="date"
              value={values.date}
              error={!!errors.date}
              onChange={(e) => set("date", e.target.value)}
              slotProps={{ htmlInput: { "aria-describedby": describedBy("gn-date", errors.date) } }}
            />
          </Field>

          {/*
            確認人（設計稿 L1725-1729）。project_phase 補了 confirmer 欄位之後，
            這一欄是真的存得進去的，所以說明只要講清楚兩件事：存進去之後看得到哪裡、
            以及「不會通知對方」。第二句非寫不可 —— 選了一個人卻沒有人告訴他，
            使用者會以為交代出去了。不留白也不寫成「已送交確認」。
          */}
          <Field
            htmlFor="gn-who"
            label="確認人"
            help="選定後，時程軸的里程碑卡將顯示確認人。系統不會通知對方，也不提供確認操作，請自行告知對方。尚未決定時請保留「尚未決定」。"
            mark="opt"
            helpId="gn-who-help"
          >
            <TextField
              select
              fullWidth
              id="gn-who"
              value={values.owner}
              onChange={(e) => set("owner", e.target.value)}
              slotProps={{ select: { native: true }, htmlInput: { "aria-describedby": describedBy("gn-who") } }}
            >
              <option value="">尚未決定</option>
              {memberOptions.map((m) => (
                <option key={m.account} value={m.account}>
                  {m.label}
                </option>
              ))}
            </TextField>
          </Field>

          <Field
            htmlFor="gn-note"
            label="達成標準"
            mark="req"
            help="供 RD 主管參考，例如「收銀、折扣、對帳三項功能可完整執行一輪」。"
            error={errors.note}
            helpId="gn-note-help"
            errorId="gn-note-err"
          >
            <TextField
              placeholder="例如：收銀、折扣、對帳三項功能可完整執行一輪"
              fullWidth
              multiline
              minRows={3}
              id="gn-note"
              value={values.note}
              error={!!errors.note}
              onChange={(e) => set("note", e.target.value)}
              slotProps={{ htmlInput: { "aria-describedby": describedBy("gn-note", errors.note) } }}
            />
          </Field>

          {/*
            提醒門檻（設計稿 L1736 在這裡畫了一個下拉）。

            門檻現在真的存得起來了，但它存在 system_setting 的 goal.buffer_days，
            是全公司共用的一個數字 —— ProjectPhase 仍然沒有欄位，所以「這個里程碑自己的門檻」
            還是存不進去。畫成下拉的話，選了不會有任何事發生：一個按了沒反應的控制項
            比一行字更會誤導人。改成把現在生效的值寫出來，並指路到真的改得動的地方
            （同 Preferences 在沒有權限時的作法：改不了就給一行字，不給一個停用的欄位）。
          */}
          <Field
            label="提醒門檻"
            help={
              bufferLoaded
                ? "此門檻為全公司共用，非各里程碑個別設定。如需變更，請至「設定 → 偏好設定」的「里程碑的提醒門檻」。"
                : bufferLoading
                  ? "此門檻為全公司共用，非各里程碑個別設定。目前生效的值讀取中，下方暫時顯示預設值。"
                  : "此門檻為全公司共用，非各里程碑個別設定。目前無法讀取設定值，下方顯示為預設值；如需變更，請至「設定 → 偏好設定」。"
            }
          >
            <Typography sx={{ fontWeight: 700 }}>{bufferDays} 天</Typography>
          </Field>

          {/* 重要後果寫成警示 Banner，不是按鈕底下的灰字小字（硬規則 4） */}
          <Banner kind="warn" icon={<MdErrorOutline />}>
            <strong>{isEdit ? "儲存後的影響　" : "建立後的影響　"}</strong>
            {isEdit
              ? `變更將立即反映於時程軸，但目前不會自動通知任何人，所屬工項的負責人不會得知日期已變更，請自行告知。黃燈門檻仍為 ${bufferDays} 天。${bufferSrc}`
              : warnBody}
          </Banner>

          {/* 整頁只有這一顆主要按鈕（硬規則 1）。
              送出中要擋住第二次點擊：建立里程碑會同步寫活動紀錄與推播，慢的時候要好幾秒 */}
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <Button variant="contained" type="button" onClick={submit} disabled={sending} loading={sending} loadingPosition="start">
              {sending ? "送出中…" : isEdit ? "儲存" : "建立里程碑"}
            </Button>
            <Button variant="text" type="button" onClick={back}>
              取消
            </Button>
          </Stack>
        </Stack>
        </CardContent>
      </Card>
      ) : null}

      {/* ---------- 這個專案已經有的里程碑（設計稿 L1753-1783） ---------- */}
      {/* 一個都沒有時整塊不畫（硬規則 2）：一張寫著「目前沒有里程碑」的空卡片不值得佔一個畫面 */}
      {ready && view.goals.length > 0 && tab === "list" ? (
        <Card sx={{ mt: "var(--space-6)" }}>
          <CardContent>
          <Typography variant="h3" component="h2">
            {proj ? `${proj.name} 的既有里程碑` : "本專案的既有里程碑"}
          </Typography>
          {/*
            設計稿這裡寫「你只能改自己訂的里程碑；別人訂的要請他改」，這一版仍然不能照抄。
            訂定者現在讀得回來了（create_name），但那句話講的是一條後端根本沒有的規則：
            PhaseController 的 PUT 只看 schedule.edit 這個權限，不看是誰建的 ——
            照抄之後使用者會發現他改得動別人的里程碑，然後就不信畫面上其他的字了。
            所以每一列一律寫「修改」（見 GoalRow 的 note），只把「誰訂的」寫出來，
            要不要動別人的里程碑交給人判斷。
          */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-1)" }}>
            {"各列皆標示建立者。具備權限即可編輯他人建立的里程碑，編輯前請先告知對方。"}
          </Typography>

          {/* 清單貼齊外層卡片左右緣，上方一條分隔線跟說明隔開（不另包一層卡片） */}
          <Box sx={{ mt: "var(--space-4)", mx: -2, borderTop: 1, borderColor: "divider" }}>
            <List disablePadding>
            {view.goals.map((g) => {
              const isThis = editing && String(g.id) === String(editing.id);
              return (
                <GoalRow
                  key={g.id}
                  tone={g.level === "attention" || g.level === "warn" ? g.level : ""}
                  title={`${g.endYmd ? longDate(g.endYmd) : "未設定里程碑日期"} · ${g.name}`}
                  /* 三格照設計稿 L1760 的順序：誰訂的、哪天訂的、幾件工項。
                     換不到姓名的那一筆就少一格，不補「不明」之類的字。
                     確認人不放進來 —— 這一列已經有日期、名稱、三格 meta 與一個徽章，
                     再加一格就沒有人看得完；確認人在里程碑卡與這一頁的表單裡都看得到 */
                  metas={[
                    g.createdBy ? `建立者：${g.createdBy}` : "",
                    g.createdYmd ? `建立於 ${longDate(g.createdYmd)}` : "",
                    g.taskCount > 0 ? `${g.taskCount} 件工項` : "尚未安排工項",
                  ].filter(Boolean)}
                  tag={goalTag(g)}
                  note={isThis ? "編輯中" : "編輯"}
                  onClick={() => {
                    if (isThis) return;
                    /* 同一頁換成修改那一筆：網址換了，上面的 useEffect 會把值鋪進表單 */
                    navigate(`/Schedule/Goal/New?projectId=${encodeURIComponent(projSel)}&phaseId=${g.id}`);
                    /* 網址換了但還在同一頁，不切分頁的話按下去畫面不會動 */
                    setTab("form");
                  }}
                />
              );
            })}
            </List>
          </Box>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}

/*
 * 資料來源（零新端點）
 *   · GET_ProjectList          → 專案下拉
 *   · GET_PhaseList(projectId) → state.Phase.List，既有里程碑與修改時的初值
 *   · GET_ScheduleWorkItems    → state.Schedule.WorkItems，既有里程碑的黃／紅燈號
 *   · GET_ProjectMembers       → state.Project.Members，「確認人」下拉的帳號與姓名
 *   · POST_Phase / PUT_Phase   → 建立／修改（純 FormData，不是 data=<JWT>）
 *   · useGoalBufferDays        → GET /Setting/Goal，黃燈的門檻天數
 *
 * 還剩下的誠實缺口（畫面上明寫，不假裝）：
 *   · 選了確認人，系統不會通知他，也沒有「我確認了」可以按 —— 那是另一件事
 *     （要有一個確認動作與它的時間戳），現在只有「寫下來是誰」。欄位說明有寫。
 *   · 訂定者與確認人的姓名是後端拿帳號去 Admin 表換的，帳號被刪掉就換不回來，
 *     這時候畫面上寫的是帳號（見 personLabel），不編名字。
 *   · 提醒緩衝天數：門檻存得起來，但存在 system_setting 的 goal.buffer_days，
 *     是全公司共用的一個數字；「每個里程碑各自一個門檻」仍然沒有欄位，
 *     所以這一頁只顯示現在生效的值、不給控制項。
 *
 * （原本列在這裡的「確認人存不進去」與「訂定者讀不回來」都補上了：
 *   project_phase 多了 confirmer 欄位，/Phase/List 的投影多回
 *   confirmer / confirmer_name / create_id / create_name。）
 */
const mapStateToProps = (state) => ({
  projectList: _.get(state, "Project.List", []),
  phaseList: _.get(state, "Phase.List", []),
  workItems: _.get(state, "Schedule.WorkItems", []),
  members: _.get(state, "Project.Members", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ProjectList: (payload, callback, loading) => dispatch({ type: "GET_ProjectList", payload, callback, loading }),
  GET_PhaseList: (projectId, callback, loading) => dispatch({ type: "GET_PhaseList", projectId, callback, loading }),
  GET_ScheduleWorkItems: (payload, callback, loading) =>
    dispatch({ type: "GET_ScheduleWorkItems", payload, callback, loading }),
  GET_ProjectMembers: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectMembers", payload, callback, loading }),
  POST_Phase: (projectId, formData, callback, loading) =>
    dispatch({ type: "POST_Phase", projectId, formData, callback, loading }),
  PUT_Phase: (phaseId, formData, callback, loading) =>
    dispatch({ type: "PUT_Phase", phaseId, formData, callback, loading }),
});

ScheduleGoalNew.propTypes = {
  projectList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  phaseList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  workItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  members: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_ProjectList: PropTypes.func,
  GET_PhaseList: PropTypes.func,
  GET_ScheduleWorkItems: PropTypes.func,
  GET_ProjectMembers: PropTypes.func,
  POST_Phase: PropTypes.func,
  PUT_Phase: PropTypes.func,
};

ScheduleGoalNew.defaultProps = {
  projectList: [],
  phaseList: [],
  workItems: [],
  members: [],
  GET_ProjectList: () => {},
  GET_PhaseList: () => {},
  GET_ScheduleWorkItems: () => {},
  GET_ProjectMembers: () => {},
  POST_Phase: () => {},
  PUT_Phase: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleGoalNew);
